import {
  DeleteOutlined,
  FlagOutlined,
  PlusOutlined,
  RightOutlined,
  WarningFilled
} from "@ant-design/icons"
import { Button, Flex, Space, Tag, Tooltip, Typography } from "antd"
import type { ItemType } from "antd/es/menu/interface"
import { type ColumnsType } from "antd/es/table"
import dayjs from "dayjs"
import React from "react"

import Icon from "../../../../../../../Icon"
import { empty } from "../../../../../../../constants/placeholders"
import { budgetFormat } from "../../../../../../../utils/number/budget-format.ts"
import {
  type Attachment,
  AttachmentModels
} from "../../../../../../Album/types/attachment.types.ts"
import { ALL_DATE_FORMATS } from "../../../../../../TeamManagementPage/constants/date-formats.ts"
import { AttachmentsController } from "../../../../../components/AttachmentsController.tsx"
import { RowActionsMenu } from "../../../../../../../components/EntityTable/RowActionsMenu.tsx"
import type { FinancialTransactionItem } from "../../../../../types/project-finance.types.tsx"
import {
  ENTRY_TYPE_ICONS,
  ENTRY_TYPE_LABELS,
  FINANCE_ITEM_STATUSES,
  FINANCE_ITEM_STATUSES_MAP,
  FINANCE_ITEM_TYPES,
  FINANCE_ITEM_TYPES_MAP,
  isFinanceItemLocked,
  type EntryType,
  type FinanceItemStatuses
} from "../../../../constants/finance-item-constants.ts"
import { PaymentsSummary } from "./PaymentsSummary.tsx"

const { Text, Paragraph } = Typography

type Translate = (key: string, options?: Record<string, unknown>) => string

interface GetColumnsColumnsArgs {
  t: Translate
  currentUserId: string
  isLocked: boolean
  projectId: string
  userDateFormat: string
  //
  isAdding?: boolean
  editAllowed?: boolean
  deleteAllowed?: boolean
  uploadFileAllowed?: boolean
  deleteFileAllowed?: boolean
  deleteForeignFileAllowed?: boolean
  generateInvoiceAllowed?: boolean
  //
  onSelectRow: (record: FinancialTransactionItem, action: string) => void
  onCompletePayment: (record: FinancialTransactionItem) => void
  onOpenPayments: (record: FinancialTransactionItem) => void
}

export const getColumns = ({
  t,
  currentUserId,
  projectId,
  isLocked,
  userDateFormat,
  isAdding,
  editAllowed,
  deleteAllowed,
  deleteForeignFileAllowed,
  uploadFileAllowed,
  deleteFileAllowed,
  generateInvoiceAllowed,
  onSelectRow,
  onCompletePayment,
  onOpenPayments
}: GetColumnsColumnsArgs): ColumnsType<FinancialTransactionItem> => [
  {
    title: "Payment Date",
    dataIndex: "date",
    key: "date",
    sorter: true,
    width: 135,
    render: (value: string, record) => {
      const formats = userDateFormat || ALL_DATE_FORMATS
      const parsedDate = dayjs(record?.date, formats, true)
      const parsedDue = dayjs(record?.due_date, formats, true)
      const backDated =
        parsedDate.isValid() &&
        parsedDue.isValid() &&
        parsedDate.isAfter(parsedDue, "day")
      return (
        <Space>
          <Text>{value || empty}</Text>
          {backDated && (
            <Tooltip title="Date is after due date">
              <WarningFilled className="text-warning" />
            </Tooltip>
          )}
        </Space>
      )
    }
  },
  {
    title: "Financial Item",
    dataIndex: "name",
    key: "name",
    width: 180,
    render: (text: string) => <Text ellipsis={{ tooltip: text }}>{text}</Text>
  },
  {
    title: "Type",
    dataIndex: "item_type_display",
    key: "item_type_display",
    width: 135,
    render: (type: string, record) => {
      const config = FINANCE_ITEM_TYPES_MAP?.[record?.item_type]

      return (
        <Tag style={config?.style} className="m-0">
          {type}
        </Tag>
      )
    }
  },
  {
    title: "Entry Type",
    dataIndex: "entry_type",
    key: "entry_type",
    width: 160,
    render: (value: EntryType, record) => {
      const label =
        record?.entry_type_display || ENTRY_TYPE_LABELS[value] || value
      const IconComponent = value ? (ENTRY_TYPE_ICONS[value] ?? null) : null

      return (
        <Space size={4}>
          {IconComponent && <IconComponent />}
          <Text>{label || empty}</Text>
        </Space>
      )
    }
  },
  {
    title: "Due Date",
    dataIndex: "due_date",
    key: "due_date",
    sorter: true,
    width: 135,
    render: (value: string) => {
      const parsedDate = dayjs(value, userDateFormat || ALL_DATE_FORMATS, true)
      const isOverdue = parsedDate && parsedDate.isBefore(dayjs().tz(), "day")

      return (
        <Space>
          <Text>{value || empty}</Text>
          {isOverdue && (
            <Tooltip title={"Overdue"}>
              <WarningFilled className={"text-warning"} />
            </Tooltip>
          )}
        </Space>
      )
    }
  },
  {
    title: "Comments",
    dataIndex: "comment",
    key: "comment",
    width: 200,
    onCell: () => ({ style: { maxWidth: 200 } }),
    render: (description: string) => (
      <Paragraph
        className={"mb-0"}
        ellipsis={{ rows: 3, tooltip: description }}
      >
        {description || empty}
      </Paragraph>
    )
  },
  {
    title: "Committed Value USD",
    dataIndex: "value",
    key: "value",
    width: 145,
    render: (value: number | string, record: FinancialTransactionItem) => {
      const planned = typeof value === "string" ? parseFloat(value) : value
      const paid = parseFloat(record.exercised_value ?? "0")
      const hasPayments = (record.partial_finances?.length ?? 0) > 0
      if (hasPayments && Number.isFinite(paid) && paid !== 0) {
        return <Text>{`${budgetFormat(paid)} / ${budgetFormat(planned)}`}</Text>
      }
      return <Text>{budgetFormat(planned)}</Text>
    }
  },
  {
    title: "Source/Target",
    dataIndex: "responsible_type_display",
    key: "responsible_type_display",
    width: 150,
    render: (value: string) => value || empty
  },
  {
    //todo ????
    title: "Name",
    dataIndex: "custom_subcontractor_name",
    key: "custom_subcontractor_name",
    width: 250,
    ellipsis: true,
    render: (customName, record) => {
      const name =
        customName || record?.subcontractor_name || record?.assignee_name

      if (!name && record?.subcontractor) {
        return "Unnamed"
      }

      return name || empty
    }
  },
  {
    title: "Project Type",
    dataIndex: "project_type_display",
    key: "project_type_display",
    width: 200,
    render: (value: string | null) => value || empty
  },
  {
    title: "Milestone",
    // WP3.11: sortable by milestone order. dataIndex drives the ?ordering field
    // (project_phase__order) sent to the backend; the cell still shows the name.
    dataIndex: "project_phase__order",
    key: "project_phase_name",
    width: 200,
    ellipsis: true,
    sorter: true,
    render: (_, record) => (
      <Text ellipsis={{ tooltip: record?.project_phase_name }}>
        {record?.project_phase_name || empty}
      </Text>
    )
  },
  {
    title: "Invoice",
    dataIndex: "invoice_display",
    key: "invoice_display",
    width: 200,
    render: (value: string | null) => value || empty
  },
  {
    title: "Payment Method",
    dataIndex: "payment_method_display",
    key: "payment_method_display",
    width: 150,
    render: (value: string | null) => value || empty
  },
  {
    title: "Payment Details",
    dataIndex: "payment_details",
    key: "payment_details",
    width: 180,
    render: (value: string | null) => value || empty
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    width: 140,
    fixed: "right",
    render: (status: FinanceItemStatuses, record) => {
      const statusData = FINANCE_ITEM_STATUSES_MAP?.[status]

      return (
        <Tag style={statusData?.style} className="m-0">
          {record?.status_display}
          {record?.is_planned && status === FINANCE_ITEM_STATUSES.PLANNED && (
            <Tooltip title="Initially planned">
              <FlagOutlined className="ml-1 text-xs " />
            </Tooltip>
          )}
        </Tag>
      )
    }
  },
  {
    title: "Payments",
    key: "payments",
    dataIndex: "partial_finances",
    width: 150,
    fixed: "right",
    render: (_: unknown, record) => {
      const count = record.partial_finances?.length ?? 0

      if (count === 0) {
        // Recording a payment is a write on the line, so it answers to the
        // same right as the row's own edit, which an archive has cleared.
        if (!editAllowed || isFinanceItemLocked(record?.status)) {
          return <Text type="secondary">-</Text>
        }
        return (
          <Button
            type="link"
            size="small"
            className="px-0"
            icon={<PlusOutlined />}
            onClick={() => onOpenPayments(record)}
          >
            Add Payment
          </Button>
        )
      }

      return (
        <Button
          type="link"
          size="small"
          className="h-auto px-0 text-left"
          onClick={() => onOpenPayments(record)}
        >
          <Flex vertical align="flex-start" className="leading-tight">
            <Space size={4}>
              <Text>{`${count} ${count === 1 ? "payment" : "payments"}`}</Text>
              <RightOutlined className="text-xs" />
            </Space>
            <PaymentsSummary record={record} compact />
          </Flex>
        </Button>
      )
    }
  },
  {
    title: "Attachments",
    key: "attachments",
    dataIndex: "attachments",
    width: 100,
    align: "center",
    fixed: "right",
    render: (attachments: Attachment[], record) => (
      <AttachmentsController
        currentUserId={currentUserId}
        // Locked items keep files viewable but offer no new uploads.
        addAllowed={uploadFileAllowed && !isFinanceItemLocked(record?.status)}
        deleteAllowed={deleteFileAllowed}
        deleteForeignAllowed={deleteForeignFileAllowed}
        project_id={projectId}
        model_type={AttachmentModels.FINANCIAL_ITEM}
        object_id={record?.id}
        attachments={attachments}
      />
    )
  },
  {
    title: "Actions",
    key: "actions",
    width: 90,
    align: "center",
    fixed: "right",
    hidden: !editAllowed && !deleteAllowed && !isAdding,
    render: (_, record) => {
      if (!editAllowed && !deleteAllowed) return null
      return (
        <Flex gap={"small"} justify={"center"} className={"w-20 mx-auto"}>
          <RowActionsMenu
            disabled={isLocked}
            items={
              [
                ...(editAllowed
                  ? [
                      {
                        key: "edit_row",
                        label: "Edit",
                        icon: <Icon type={"Edit"} />,
                        onClick: () => onSelectRow(record, "edit_row")
                      }
                    ]
                  : []),
                ...(editAllowed &&
                record?.status !== FINANCE_ITEM_STATUSES.COMPLETED &&
                record?.status !== FINANCE_ITEM_STATUSES.CANCELLED
                  ? [
                      {
                        key: "complete_payment",
                        label: "Complete payment",
                        icon: <FlagOutlined />,
                        onClick: () => onCompletePayment(record)
                      }
                    ]
                  : []),
                ...(generateInvoiceAllowed &&
                record?.item_type === FINANCE_ITEM_TYPES.RECEIVABLE
                  ? [
                      {
                        key: "generate_invoice",
                        label: t("finance.actions.generateInvoice"),
                        icon: <Icon type={"Dollar"} />,
                        onClick: () => onSelectRow(record, "generate_invoice")
                      }
                    ]
                  : []),
                ...(deleteAllowed
                  ? [
                      {
                        key: "delete_row",
                        label: "Delete",
                        icon: <DeleteOutlined />,
                        danger: true,
                        onClick: () => onSelectRow(record, "delete_row")
                      }
                    ]
                  : [])
              ] as ItemType[]
            }
          />
        </Flex>
      )
    }
  }
]
